import { HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { Logger } from '../../logger/logger';
import { Observable, catchError, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { resolveErrorCodeMessage } from '../../errors/errors';

const log = new Logger('ErrorHandlerInterceptor');

/**
 * URL fragments that should never trigger error toasts (e.g. health checks, silent polling).
 * Add partial URL strings here; any request whose URL includes one of these is silently skipped.
 */
const SILENT_URLS: string[] = ['/health', '/api/health', '/notification/sse', '/refresh-token'];

/**
 * Endpoints whose caller renders its OWN error message inline, so a toast here would
 * report the same failure twice — and the two can disagree (login showed the toast's
 * "account locked" next to the form's generic "login failed", reported 2026-08-30).
 * Unlike SILENT_URLS these still get the full error object rethrown, because the
 * caller needs `error.errors[].errorCode` to pick its message.
 */
const SELF_HANDLED_URLS: string[] = [
  'account/authenticate',
  // Tỉ giá thị trường: màn chứng từ gọi mỗi lần đổi tiền tệ và LẶNG LẼ giữ ô nhập tay khi lỗi
  // (MarketRateService); màn tỉ giá tự báo lỗi. Toast ở đây sẽ bật lên mỗi lần đổi tiền tệ.
  'currency/refresh-market-rates',
  // Khấu trừ thuế GTGT: màn chi tiết kỳ gọi XEM TRƯỚC mỗi lần mở trang. Người không có quyền nhận
  // 403 — khối VAT tự ẩn, không bật toast mỗi lần mở; lỗi khác khối tự vẽ (VatOffsetCardComponent).
  'vietnam/vat/offset',
  // Kiểm trước khoá sổ: màn chi tiết kỳ gọi mỗi lần mở trang — 403 thì khối tự ẩn, lỗi khác khối
  // tự vẽ (PreCloseCheckCardComponent).
  'vietnam/period/validate-pre-close',
  // Tạo trích trước cước: lưu là GHI SỔ ngay (US-30, fail-closed) — hộp thoại tự vẽ lỗi, kèm câu
  // chỉ đường cho ERR_EXCHANGE_RATE_NOT_FOUND / ERR_GL_POSTING_FAILED
  // (FreightAccrualCreateComponent). Toast ở đây sẽ báo cùng một lỗi hai lần.
  'freight-accrual/create-or-update-freight-accrual',
  // Danh mục panel bối cảnh: lỗi (404 khi FE lên trước BE) ⇒ panel lặng lẽ rơi về ba danh sách cũ
  // (WorkingContextCatalogService). Toast ở đây bật lên MỖI lần mở panel.
  'org-context/get-context-catalog',
  // Tháp điều khiển Bán hàng v2: mỗi khối gọi một endpoint riêng và TỰ vẽ trạng thái lỗi / rỗng
  // (404 khi FE lên trước BE ⇒ khối thu gọn). Toast ở đây bật tới tám lần mỗi lần đổi kỳ.
  'sales/control-tower/overview',
  'sales/control-tower/history',
  'sales/control-tower/delivery',
  'sales/control-tower/credit',
  'sales/control-tower/bu-matrix',
  'sales/control-tower/channels',
  'sales/control-tower/team',
  'sales/control-tower/exceptions',
  // US-UI-42a — dải KPI danh sách hoá đơn TỰ ẨN khi lỗi (404 khi FE lên trước BE). Toast ở đây
  // bật lên mỗi lần lọc / đổi trang.
  'sales/invoices/get-kpi',
];

/**
 * Mã lỗi hạ tầng (cổng/proxy/máy chủ quá tải) mà — nếu xảy ra LÚC TẢI TRANG — đưa người dùng sang
 * trang lỗi toàn màn `/server-error?code=<mã>` thay vì để lại một trang rỗng kèm toast.
 */
const PAGE_LOAD_SERVER_ERROR_CODES: readonly number[] = [502, 503, 504];

/** Đường dẫn trang lỗi máy chủ (route `server-error` trong app.routes.ts). */
const SERVER_ERROR_PATH = '/server-error';

/**
 * Request KHÔNG bao giờ điều hướng sang trang lỗi, kể cả lúc tải trang: health/version của màn
 * đăng nhập (màn tự vẽ vạch sóng trạng thái — rời màn vì health đỏ sẽ giấu chính chỉ báo đó).
 * SILENT_URLS và SELF_HANDLED_URLS cũng bị loại (xem `shouldNavigateToServerError`).
 */
const NO_SERVER_ERROR_PAGE_URLS: string[] = ['/api/health', '/api/version', '/version'];

/**
 * "Lúc tải trang" — đo TẠI THỜI ĐIỂM PHÁT request, không phải lúc lỗi về:
 *   (a) app chưa có lần điều hướng thành công nào (`router.navigated === false` — trước
 *       NavigationEnd đầu tiên: khởi động app, guard/resolver của trang đầu); hoặc
 *   (b) Router đang có một điều hướng dở (`router.currentNavigation() !== null` — giữa
 *       NavigationStart và NavigationEnd/Cancel/Error: guard, resolver, nạp dữ liệu trước khi vào trang).
 * Ngoài hai trường hợp đó là "đang thao tác" ⇒ chỉ toast như cũ.
 */
export function isPageLoadRequest(router: Router): boolean {
  return !router.navigated || router.currentNavigation() !== null;
}

/** Đang ở (hoặc đang đi tới) trang lỗi máy chủ ⇒ không điều hướng lại, tránh vòng lặp. */
function isOnServerErrorPage(router: Router): boolean {
  const target = router.currentNavigation()?.extractedUrl?.toString() ?? '';
  return router.url.startsWith(SERVER_ERROR_PATH) || target.startsWith(SERVER_ERROR_PATH);
}

export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);
  const router = inject(Router);
  // Chụp trạng thái NGAY khi request phát ra: khi lỗi về, điều hướng có thể đã kết thúc.
  const duringPageLoad = isPageLoadRequest(router);
  return next(req).pipe(
    catchError((err) =>
      errorHandler(err, req.url, toastService, translateService, router, duringPageLoad),
    ),
  );
};

/**
 * Lỗi 502/503/504 của một request phát ra lúc tải trang ⇒ sang trang lỗi toàn màn. Chỉ khi thân
 * lỗi KHÔNG mang mã nghiệp vụ (`errors[].errorCode`): 503 có mã là lỗi của một tính năng cụ thể,
 * nó vẫn đi đường toast có dịch mã như cũ.
 */
function shouldNavigateToServerError(
  err: HttpErrorResponse,
  requestUrl: string,
  router: Router,
  duringPageLoad: boolean,
): boolean {
  if (!duringPageLoad || !PAGE_LOAD_SERVER_ERROR_CODES.includes(err.status)) return false;
  const excluded = [...SILENT_URLS, ...SELF_HANDLED_URLS, ...NO_SERVER_ERROR_PAGE_URLS];
  if (excluded.some((url) => requestUrl.includes(url))) return false;
  if (extractErrorCodes(err).length > 0) return false;
  return !isOnServerErrorPage(router);
}

/**
 * Shows a single toast notification based on the toast type.
 * Extracted to reduce duplication.
 */
function showToast(
  toastService: ToastService,
  message: string,
  type: 'error' | 'warning' | 'info' = 'error',
): void {
  toastService.open({
    message,
    configs: {
      payload: { type },
    },
  });
}

/**
 * Extracts a human-readable error message from the server response body.
 * Supports common .NET API response shapes:
 *   - { Message: "..." }
 *   - { message: "..." }
 *   - { title: "..." }           (ProblemDetails)
 *   - { errors: { field: [...] } } (FluentValidation / ModelState)
 *   - { Errors: [{ ErrorCode, Message }] } (custom app errors)
 */
function extractServerMessage(error: HttpErrorResponse): string | null {
  const body = error.error;
  if (!body || typeof body !== 'object') {
    return null;
  }

  // Direct message properties
  if (typeof body.Message === 'string' && body.Message.trim()) {
    return body.Message;
  }
  if (typeof body.message === 'string' && body.message.trim()) {
    return body.message;
  }
  if (typeof body.title === 'string' && body.title.trim()) {
    return body.title;
  }

  // FluentValidation / ModelState: { errors: { fieldName: ["msg1", "msg2"] } }
  if (body.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)) {
    const messages: string[] = [];
    for (const field of Object.keys(body.errors)) {
      const fieldErrors = body.errors[field];
      if (Array.isArray(fieldErrors)) {
        messages.push(...fieldErrors.filter((m: unknown) => typeof m === 'string'));
      }
    }
    if (messages.length > 0) {
      return messages.slice(0, 3).join('. '); // Cap at 3 to avoid huge toasts
    }
  }

  // Backend ApiResponse format: { errors: [{ errorCode, propertyName, errorMessage }] }
  if (Array.isArray(body.errors) && body.errors.length > 0) {
    const messages: string[] = body.errors
      .map(
        (e: { errorMessage?: string; ErrorMessage?: string }) => e.errorMessage || e.ErrorMessage,
      )
      .filter((m: unknown): m is string => typeof m === 'string' && m.trim().length > 0);
    if (messages.length > 0) {
      return messages.slice(0, 3).join('. ');
    }
  }

  return null;
}

/**
 * Extracts structured error codes from the server response body so components can
 * branch on them (e.g. POS_NO_FACILITY_CONTEXT → dedicated empty state).
 * Supports both envelope casings: { Errors: [{ ErrorCode }] } and { errors: [{ errorCode }] }.
 */
function extractErrorCodes(err: HttpErrorResponse): string[] {
  const body = err.error;
  if (!body || typeof body !== 'object') {
    return [];
  }
  const structured: Array<{ ErrorCode?: string; errorCode?: string }> = Array.isArray(body.Errors)
    ? body.Errors
    : Array.isArray(body.errors)
      ? body.errors
      : [];
  return structured
    .map((e) => e?.ErrorCode || e?.errorCode)
    .filter((c): c is string => typeof c === 'string' && c.trim().length > 0);
}

// Customize the default error handler here if needed
function errorHandler(
  err: HttpErrorResponse,
  requestUrl: string,
  toastService: ToastService,
  translateService: TranslateService,
  router: Router,
  duringPageLoad: boolean,
): Observable<HttpEvent<unknown>> {
  // Lỗi hạ tầng lúc tải trang: trang lỗi toàn màn tự giải thích ⇒ KHÔNG toast thêm. Đối tượng lỗi
  // ném lại giữ nguyên hợp đồng {status,message,error,errorCodes} như mọi nhánh khác.
  if (shouldNavigateToServerError(err, requestUrl, router, duringPageLoad)) {
    log.error('Server error during page load', err);
    router.navigateByUrl(`${SERVER_ERROR_PATH}?code=${err.status}`);
    return throwError(() => ({
      status: err.status,
      statusText: err.statusText,
      message: err.message,
      error: err.error,
      errorCodes: extractErrorCodes(err),
    }));
  }

  // Skip toast for silent endpoints
  if (SILENT_URLS.some((url) => requestUrl.includes(url))) {
    return throwError(() => ({
      status: err.status,
      statusText: err.statusText,
      message: err.message,
      error: err.error,
      errorCodes: extractErrorCodes(err),
    }));
  }

  // Track whether we already showed a toast to avoid double-toasting.
  // Seeding it from SELF_HANDLED_URLS suppresses every toast branch below while still
  // letting the rich error object through to the caller at the end of this function.
  let toastShown = SELF_HANDLED_URLS.some((url) => requestUrl.includes(url));

  // Server correlation id (set by the backend on every error envelope). Appended to the toast so the
  // user can quote a single code to support — they grep SystemError / logs by it. Null on network errors.
  // Backend serializes the envelope in camelCase (`correlationId`, ExceptionHandlingMiddleware
  // ResolveJsonOptions); reading only `CorrelationId` meant the ref code never reached the toast.
  const rawCorrelationId: unknown = err.error?.correlationId ?? err.error?.CorrelationId;
  const correlationId =
    typeof rawCorrelationId === 'string' && rawCorrelationId.trim() ? rawCorrelationId : null;
  const withRef = (msg: string): string =>
    correlationId
      ? `${msg} ${translateService.instant('Errors.RefCode', { code: correlationId })}`
      : msg;

  // --- Structured error codes (custom app errors) take highest priority ---
  // Accept both envelope casings: { Errors: [{ ErrorCode, ErrorMessage }] } and
  // { errors: [{ errorCode, errorMessage }] } (System.Text.Json camelCase default).
  const rawErrors: Array<{
    ErrorCode?: string;
    ErrorMessage?: string;
    errorCode?: string;
    errorMessage?: string;
  }> = Array.isArray(err.error?.Errors)
    ? err.error.Errors
    : Array.isArray(err.error?.errors)
      ? err.error.errors
      : [];
  const errors = rawErrors
    .map((e) => ({
      code: e?.ErrorCode || e?.errorCode,
      message: e?.ErrorMessage || e?.errorMessage,
    }))
    .filter((e) => e.code || e.message);
  if (errors.length > 0 && !toastShown) {
    // Deduplicate messages and cap at 3
    const messages = new Set<string>();
    errors.forEach((error) => {
      // Translated code first (hand map, then the `Errors.Codes.*` convention). The backend
      // message is ENGLISH-ONLY, so using it while a translation exists ignores the user's
      // language setting — it is a last resort, not a peer of the translation.
      const msg =
        resolveErrorCodeMessage(error.code, (key) => translateService.instant(key)) ||
        error.message ||
        translateService.instant('Errors.General');
      messages.add(msg);
    });
    const uniqueMessages = Array.from(messages).slice(0, 3);
    showToast(toastService, withRef(uniqueMessages.join('. ')));
    toastShown = true;
  }

  // --- Status-specific handling (only if no structured errors were shown) ---
  if (!toastShown) {
    switch (true) {
      case err.status === 0:
        // Network error / CORS / connection refused
        log.error('A client-side or network error occurred.');
        showToast(toastService, translateService.instant('Errors.NetworkError'));
        toastShown = true;
        break;

      case err.status === 400: {
        // Validation error -- try to show the server's message
        const serverMsg = extractServerMessage(err);
        showToast(
          toastService,
          withRef(serverMsg || translateService.instant('Errors.ValidationError')),
        );
        toastShown = true;
        break;
      }

      case err.status === 401:
        // Don't show toast for 401 — authInterceptor (outer) handles refresh + retry.
        // Showing "Session Expired" here is premature since the token may be
        // silently refreshed and the request retried without the user noticing.
        // Only auth-related URLs (/authenticate, /refresh-token) would indicate
        // a real auth failure, but those are already in SILENT_URLS.
        break;

      case err.status === 403:
        // Chỉ tới đây khi thân 403 KHÔNG mang mã lỗi — tức từ chối quyền của ENDPOINT
        // (`RolePermissionHandler` → `context.Fail()` → ForbidAsync mặc định, thân RỖNG) hoặc
        // `OrgContextMiddleware` (`{ error, message }`, không có `errors[]`). Đó là "không có quyền
        // màn này" ⇒ sang /not-authorized.
        //
        // 403 từ chối PHẠM VI (`ScopeAccessDeniedException`: có quyền nhưng kho thuộc cơ sở khác)
        // mang `errors[].errorCode = ERR_ACCESS_DENIED`, nên đã đi nhánh mã lỗi ở trên: toast
        // `Errors.Codes.ERR_ACCESS_DENIED`, KHÔNG điều hướng, người dùng ở lại biểu mẫu. Đừng
        // điều hướng ở đây bất kể `toastShown` — spec "403 từ chối phạm vi" khoá điều đó.
        //
        // If CSRF cookie is missing, this 403 is from a stale session, not a real permission denial.
        // The auth interceptor will handle logout/redirect — don't show "Access Denied" or navigate.
        if (!document.cookie.match(/XSRF-TOKEN=/)) {
          toastShown = true; // suppress toast, let auth flow handle redirect
        } else {
          showToast(toastService, translateService.instant('Errors.AccessDenied'), 'warning');
          router.navigateByUrl('/not-authorized');
          toastShown = true;
        }
        break;

      case err.status === 404:
        showToast(toastService, translateService.instant('Errors.ResourceNotFound'), 'warning');
        toastShown = true;
        break;

      case err.status === 409:
        showToast(toastService, translateService.instant('Errors.Conflict'), 'warning');
        toastShown = true;
        break;

      case err.status === 429: {
        const retryAfter = err.headers?.get('Retry-After');
        const seconds = retryAfter ? parseInt(retryAfter, 10) : 30;
        showToast(
          toastService,
          translateService.instant('Errors.TooManyRequests', { seconds }),
          'warning',
        );
        toastShown = true;
        break;
      }

      case err.status === 503:
        showToast(toastService, translateService.instant('Errors.ServiceUnavailable'));
        toastShown = true;
        break;

      case err.status >= 500:
        log.error('Request error', err);
        showToast(toastService, withRef(translateService.instant('Errors.ServerError')));
        toastShown = true;
        break;

      default:
        // Any other 4xx not handled above
        log.error('Request error', err);
        showToast(toastService, translateService.instant('Errors.General'));
        toastShown = true;
        break;
    }
  }

  // Return an observable with a user-facing error message. `errorCodes` carries the
  // structured backend codes (both `Errors`/`errors` casings) so subscribers can branch
  // on specific business errors without re-parsing the HTTP body.
  //
  // `error` forwards the ORIGINAL response body. Dropping it used to make every
  // component-level `err.error.errors[0].errorCode` mapping dead in production while its
  // specs stayed green — they built the raw HttpErrorResponse this interceptor never
  // delivers (login's six error-code cases, found 2026-08-30).
  return throwError(() => ({
    status: err.status,
    statusText: err.statusText,
    message: err.message,
    error: err.error,
    errorCodes: extractErrorCodes(err),
  }));
}
